import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-24 pb-14 text-center">
        <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-700">
          <i className="ri-error-warning-line text-4xl"></i>
        </span>
        <h1 className="text-xl font-bold text-foreground-100 mb-3">文章未找到</h1>
        <p className="text-foreground-600 mb-6">您访问的文章不存在或已被删除</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line"></i>
          </span>
          返回首页
        </Link>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
    author: { '@type': 'Person', name: article.author },
    publisher: {
      '@type': 'Organization',
      name: '91黑料',
      url: 'https://91heilia.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://91heilia.com/article/${article.id}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91heilia.com/' },
                { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://91heilia.com/category/${article.categorySlug}` },
                { '@type': 'ListItem', position: 3, name: article.title },
              ],
            },
            articleSchema,
          ]),
        }}
      />

      <article className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-14">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        {/* Header */}
        <header className="mt-6 mb-8">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Link
              to={`/category/${article.categorySlug}`}
              className="px-3 py-1 rounded-full bg-primary-500/10 text-primary-500 text-xs font-medium hover:bg-primary-500/20 transition-colors cursor-pointer"
            >
              {article.categoryName}
            </Link>
            {article.isHot && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-primary-500/15 text-primary-500 border border-primary-500/20 whitespace-nowrap">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-fire-fill text-[10px]"></i>
                </span>
                HOT
              </span>
            )}
            {article.isExclusive && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-accent-500/15 text-accent-500 border border-accent-500/20 whitespace-nowrap">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-vip-crown-fill text-[10px]"></i>
                </span>
                独家
              </span>
            )}
          </div>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-50 leading-tight">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 mt-4 text-sm text-foreground-600 flex-wrap">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-line text-xs"></i>
              </span>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-xs"></i>
              </span>
              {article.publishedAt.split('T')[0]}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-eye-line text-xs"></i>
              </span>
              {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount} 阅读
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-chat-3-line text-xs"></i>
              </span>
              {article.commentCount} 评论
            </span>
          </div>
        </header>

        {/* Featured Image */}
        <div className="w-full h-[300px] md:h-[420px] rounded-xl overflow-hidden mb-8 bg-background-200">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={`${article.title} - 91黑料`}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div
          className="prose prose-invert max-w-none
            prose-p:text-foreground-300 prose-p:leading-relaxed prose-p:text-base prose-p:mb-4
            prose-strong:text-foreground-100
            prose-a:text-primary-500
            [&_p:first-child]:text-lg [&_p:first-child]:text-foreground-200 [&_p:first-child]:font-medium"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* Tags */}
        <div className="mt-8 pt-6 border-t border-background-300/40">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-foreground-600">
              <span className="w-4 h-4 flex items-center justify-center inline-block align-middle mr-1">
                <i className="ri-price-tag-3-line text-xs"></i>
              </span>
              标签：
            </span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-background-200/70 text-xs text-foreground-500 border border-background-300/40"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="mt-10">
            <h2 className="text-lg font-bold text-foreground-100 font-heading mb-5">
              <a href="#!">相关推荐</a>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="group block bg-background-100 border border-background-300/40 rounded-xl overflow-hidden hover:border-primary-500/30 transition-all cursor-pointer"
                >
                  <div className="w-full h-[140px] overflow-hidden">
                    <img
                      src={rel.imageUrl}
                      alt={rel.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-medium text-foreground-300 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                      {rel.title}
                    </h4>
                    <p className="text-xs text-foreground-700 mt-1.5">{rel.publishedAt.split('T')[0]}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
}